import { STUDIO_TYPE_LABELS } from '@conversed/shared/src/central/types/studio.type';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Actions } from '../constants/actionTypes';
import { DataKeys } from '../constants/login';
import type { TCompany } from '../interfaces/IAuth';
import { useMainDispatch } from './useMainDispatch';
import { useMainSelector } from './useMainSelector';
import { useQueryState } from './useQueryState';

export const useCompanySelector = () => {
  const {
    user: { currentUser },
  } = useMainSelector();
  const currentUserRef = useRef(currentUser);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { query: companyStateFilter, setQuery: setCompanyStateFilter } =
    useQueryState<'all' | 'active' | 'inactive'>('state', 'active');

  const dispatch = useMainDispatch();

  // * refreshedCompanies is for callers that just awaited /me: the ref below is
  // * only synced in an effect, so it still holds the pre-refresh snapshot, and
  // * SET_USER replaces currentUser wholesale - without threading the awaited
  // * list in, the stale snapshot's companies clobber the fresh ones.
  const setSelectedCompany = useCallback(
    (selectedCompany: TCompany, refreshedCompanies?: TCompany[]) => {
      const { identifier: selectedCompanyId, permissions } = selectedCompany;
      localStorage.setItem(
        DataKeys.SELECTED_COMPANY_DATA_KEY,
        selectedCompanyId,
      );
      const previousUser = currentUserRef.current;

      if (previousUser) {
        const companies = refreshedCompanies ?? previousUser.companies;

        dispatch({
          type: Actions.SET_USER,
          payload: {
            permissions: permissions ?? [],
            currentUser: {
              ...previousUser,
              companies,
              selectedCompanyId,
            },
          },
        });
      }

      navigate('/');
    },
    [dispatch, navigate],
  );

  const openCompanySelector = useCallback(() => {
    if (currentUser) {
      dispatch({
        type: Actions.SET_USER,
        payload: {
          currentUser: {
            ...currentUser,
            selectedCompanyId: undefined,
          },
        },
      });
    }
  }, [currentUser, dispatch]);

  const selectedCompany = useMemo(
    () =>
      currentUser?.companies?.find(
        (company: TCompany) =>
          company.identifier === currentUser?.selectedCompanyId,
      ),
    [currentUser?.companies, currentUser?.selectedCompanyId],
  );

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const companiesToShow: TCompany[] = useMemo(() => {
    const companies: TCompany[] = currentUser?.companies || [];
    if (companyStateFilter === 'all') {
      return companies;
    }
    return companies.filter((company) =>
      companyStateFilter === 'active' ? company.isActive : !company.isActive,
    );
  }, [companyStateFilter, currentUser?.companies]);

  const hasUserCompanies = useMemo(
    () => currentUser?.companies?.length && currentUser.companies.length > 0,
    [currentUser?.companies],
  );

  // Get all parent companies (companies without parentCompanyId)
  const parentCompanies = useMemo(() => {
    return companiesToShow.filter((company) => !company.parentCompanyId);
  }, [companiesToShow]);

  // Group studios by parent company
  const companyStudios = useMemo(() => {
    const groups: Record<string, { parent: TCompany; studios: TCompany[] }> =
      {};
    const childStudios = companiesToShow.filter(
      (company) => company.parentCompanyId,
    );
    // First, create groups for all parent companies
    parentCompanies.forEach((parent) => {
      groups[parent.id] = {
        parent,
        studios: [parent], // Include parent itself as a studio
      };
    });

    // Then, add child studios to their respective parent groups
    childStudios.forEach((child) => {
      if (child.parentCompanyId) {
        if (groups[child.parentCompanyId]) {
          groups[child.parentCompanyId].studios.push(child);
        } else {
          // If parent doesn't exist in user's companies (edge case),
          // treat child as independent
          groups[child.id] = {
            parent: child,
            studios: [child],
          };
        }
      }
    });

    return Object.values(groups);
  }, [parentCompanies, companiesToShow]);

  // Get all studios for the currently selected company
  const selectedCompanyStudios = useMemo(() => {
    if (!selectedCompany) {
      return [];
    }

    const parentId = selectedCompany.parentCompanyId || selectedCompany.id;
    const group = companyStudios.find((group) => group.parent.id === parentId);

    return group?.studios || [];
  }, [selectedCompany, companyStudios]);

  // Get all sibling studios EXCEPT the currently selected company
  const siblingStudios = useMemo(() => {
    if (!selectedCompany) {
      return [];
    }

    return selectedCompanyStudios.filter(
      (studio) => studio.id !== selectedCompany.id,
    );
  }, [selectedCompanyStudios, selectedCompany]);

  const getParentCompanySelection = useCallback((): {
    value: string;
    description: string;
  }[] => {
    const options = [
      {
        value: '',
        description: t('new-customer.company.no-parent-company'),
      },
    ];

    // Add parent companies, excluding current company
    const filteredParents = parentCompanies.filter(
      (company) => company.id !== selectedCompany?.id,
    );

    filteredParents.forEach((company) => {
      options.push({
        value: company.id,
        description: `${company.name} - ${STUDIO_TYPE_LABELS[company.studioType]}`,
      });
    });

    return options;
  }, [parentCompanies, t, selectedCompany?.id]);

  return {
    selectedCompany,
    hasUserCompanies,
    companies: companiesToShow,
    setSelectedCompany,
    openCompanySelector,
    companyStateFilter,
    setCompanyStateFilter,
    parentCompanies,
    selectedCompanyStudios,
    getParentCompanySelection,
    siblingStudios,
  };
};
